import { motion } from "framer-motion";

import { SEO } from "@/components/common/SEO";
import { PageHero } from "@/components/sections/PageHero";
import { CTABand } from "@/components/sections/CTABand";
import { Container } from "@/components/ui/Container";
import { Section } from "@/components/ui/Section";
import { SectionHeading } from "@/components/ui/SectionHeading";
import { OUR_SITES } from "@/data/ourSites";
import { getIcon } from "@/lib/icons";
import { cardHover, staggerContainer, staggerItem, viewportOnce } from "@/lib/motion";

export default function OurSites() {
  return (
    <>
      <SEO
        title="Our Sites"
        description="Explore MedCore Research's network of research-ready community clinics, diverse patient populations, modern clinical infrastructure, and centralized SMO support."
        path="/our-sites"
      />
      <PageHero
        eyebrow="Our Sites"
        title={OUR_SITES.heading}
        description="A connected network built to support high-quality clinical trials, representative patient recruitment, and reliable site performance."
      />

      <Section data-testid="our-sites-overview">
        <Container>
          <div className="grid items-start gap-12 lg:grid-cols-[1.35fr_0.65fr] lg:gap-16">
            <div>
              <SectionHeading
                eyebrow="Our Network"
                title="Community-Based Research With the Strength of Centralized Support"
                align="left"
              />
              <div className="mt-7 space-y-5 text-base leading-8 text-muted-foreground sm:text-lg">
                {OUR_SITES.introduction.map((paragraph) => (
                  <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                ))}
              </div>
            </div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1"
            >
              {OUR_SITES.metrics.map((metric) => (
                <motion.div
                  key={metric.value}
                  variants={staggerItem}
                  className="rounded-2xl border border-primary/15 bg-primary/5 p-7"
                >
                  <p className="text-4xl font-bold tracking-tight text-primary sm:text-5xl">
                    {metric.value}
                  </p>
                  <p className="mt-2 text-sm font-medium leading-6 text-foreground">
                    {metric.label}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </Container>
      </Section>

      <Section muted data-testid="our-sites-strengths">
        <Container>
          <SectionHeading
            eyebrow="Site Capabilities"
            title="Built for Reliable Study Delivery"
            description="Our clinic network combines local patient access with consistent systems, trained teams, and responsive centralized support."
          />

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {OUR_SITES.strengths.map((strength) => {
              const Icon = getIcon(strength.icon);
              return (
                <motion.article
                  key={strength.title}
                  variants={staggerItem}
                  whileHover={{ y: -4 }}
                  transition={cardHover}
                  className="rounded-xl border border-border bg-background p-6 shadow-sm"
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon size={22} aria-hidden="true" />
                  </div>
                  <h2 className="mt-5 text-lg font-semibold text-foreground">
                    {strength.title}
                  </h2>
                  <p className="mt-3 text-sm leading-6 text-muted-foreground">
                    {strength.description}
                  </p>
                </motion.article>
              );
            })}
          </motion.div>
        </Container>
      </Section>

      <CTABand title="Looking for research-ready community sites?" />
    </>
  );
}
